import { useEffect, useRef } from 'react';

/**
 * Hook phát nhạc nền — tự loop, tự cleanup khi unmount.
 * @param src  Đường dẫn file âm thanh (vd: '/audio/lobby.mp3')
 * @param opts.volume  0–1, mặc định 0.4
 * @param opts.enabled  false để tắt hẳn (không tạo Audio object)
 * @param opts.fadeOutMs  Thời gian fade-out khi unmount (ms), mặc định 800
 */
export function useAudio(
  src: string,
  opts: { volume?: number; enabled?: boolean; fadeOutMs?: number } = {},
) {
  const { volume = 0.4, enabled = true, fadeOutMs = 800 } = opts;
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const audio = new Audio(src);
    audio.loop = true;
    audio.volume = volume;
    audioRef.current = audio;

    audio.play().catch(() => {
      // Autoplay blocked — ignore silently (browser policy)
    });

    return () => {
      if (!audioRef.current) return;
      const a = audioRef.current;
      audioRef.current = null;

      if (fadeOutMs <= 0) {
        a.pause();
        return;
      }

      // Fade out
      const startVol = a.volume;
      const step = startVol / (fadeOutMs / 50);
      const id = setInterval(() => {
        if (a.volume <= step) {
          clearInterval(id);
          a.pause();
        } else {
          a.volume = Math.max(0, a.volume - step);
        }
      }, 50);
    };
  }, [src, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync volume changes without restarting
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);
}
